import styles from './FiltersOverlay.module.scss';

import { Check } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import { Button } from '@mui/material';
import { Column, Table } from '@tanstack/react-table';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterType } from '@work4all/components/lib/dataDisplay/basic-table/types';
import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { FiltersOverlayStartPageV8 } from './components/filters-overlay-start-page/FiltersOverlayStartPageV8';

interface IFiltersOverlayContext {
  table: Table<unknown>;
  additionalFilters: Column<unknown>[];
  addAdditionalFilter: (name: string) => void;
  filterCardExpandStates: { [key: string]: boolean };
  listEntityType?: Entities;
  setFilterCardExpandStates: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean;
    }>
  >;
}

export const FilterOverlayContext = React.createContext<IFiltersOverlayContext>(
  {
    table: null,
    additionalFilters: [],
    addAdditionalFilter: () => null,
    setFilterCardExpandStates: () => null,
    filterCardExpandStates: {},
    listEntityType: undefined,
  }
);

interface IFiltersOverlayProps {
  table: Table<unknown>;
  open: boolean;
  listEntityType?: Entities;
  onClose: () => void;
}

export const FiltersOverlayV8: React.FC<IFiltersOverlayProps> = (props) => {
  const { table, open, listEntityType } = props;
  const { t } = useTranslation();

  const columns = table.getAllColumns();

  const [filterCardExpandStates, setFilterCardExpandStates] = useState<{
    [key: string]: boolean;
  }>(() => {
    return columns.reduce((acc, col) => {
      acc[col.columnDef.header as string] = false;
      return acc;
    }, {} as Record<string, boolean>);
  });

  const [additionalFilters, setAdditionalFilters] = useState(
    columns.filter((col) => {
      if (!col.getCanFilter()) return false;
      if (!col.getIsFiltered()) return false;

      const filterValue = col.getFilterValue() as any;

      if (filterValue.readOnly) return false;
      if (
        (col.columnDef as any).filterType === FilterType.ClosedStatus &&
        filterValue.value === false
      ) {
        return false;
      }

      return true;
    })
  );

  const addAdditionalFilter = (name: string) => {
    if (
      additionalFilters.findIndex((x) => x.columnDef.header === name) === -1
    ) {
      const newAdditionalFilters = [
        ...additionalFilters,
        columns.filter((x) => x.columnDef.header === name)[0],
      ];
      setAdditionalFilters(newAdditionalFilters);
    }
    setFilterCardExpandStates({ [name]: true });
  };

  const resetFilters = () => {
    table.resetColumnFilters();
  };

  return (
    <FilterOverlayContext.Provider
      value={{
        table,
        additionalFilters,
        addAdditionalFilter,
        filterCardExpandStates,
        setFilterCardExpandStates,
        listEntityType,
      }}
    >
      <NavigationOverlay
        open={open}
        classes={{
          wrapper: styles.wrapper,
        }}
        initialView={{
          title: t('COMMON.FILTERS'),
          view: <FiltersOverlayStartPageV8 />,
          breadcrumbRightElement: table.getState().columnFilters.length > 0 && (
            <div
              style={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              <Button
                color="primary"
                startIcon={<CancelIcon />}
                onClick={resetFilters}
              >
                {t('FILTERS.CLEAR_ALL_FILTERS')}
              </Button>
              <Button
                color="primary"
                startIcon={<Check />}
                onClick={props.onClose}
              >
                {t('FILTERS.APPLY')}
              </Button>
            </div>
          ),
        }}
        withBreadcrumbs={true}
        close={() => props.onClose()}
      />
    </FilterOverlayContext.Provider>
  );
};
